import React, { useEffect, useState } from "react";
import { TablePagination, Modal, Box, FormControl, OutlinedInput, styled } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useAuthFetch from "../../hooks/useAuthFetch";
import CustomTable from "../table/CustomTable";
import CustomTableRow from "../table/CustomTableRow";
import TableHeader from "../table/TableHeader";
import MainTableBody from "../table/MainTableBody";
import { LoadingTableSkeleton } from "../../utils/common";
import NoDataTable from "../table/NoDataTable";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Add, CloseOutlined, DeleteOutline, EditOutlined } from "@mui/icons-material";
import toast from "react-hot-toast";
import useDebounce from "../../hooks/useDebounce";

const CustomizeTextField = styled(OutlinedInput)`
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
`;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  padding: 4,
  borderRadius: 2,
};

const TemplateModal = ({ open, handleClose, refetch, isEdit, initialData }) => {
  const customFetch = useAuthFetch();
  const [templateName, setTemplateName] = useState("");


  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setTemplateName(initialData.name);

      } else {
        setTemplateName("");

      }
    }
  }, [open, isEdit, initialData]);

  const handleSubmit = async () => {
    if (!templateName) {
      toast.error("Template Name is required");
      return;
    }

    const url = new URL(
      `${process.env.REACT_APP_API_URL}/v1/dashboard/qnaTemplate/${isEdit ? "update" : "create"}`
    );

    const body = {
      name: templateName,

      ...(isEdit && { qnaTemplateId: initialData.qnaTemplateId }),
    };

    const method = isEdit ? "PUT" : "POST"; // Both seem to be POST mostly, checking create was POST. Assuming update is also POST often in this codebase or PUT. Plan said POST.

    const { status, message } = await customFetch(url, method, body);

    if (status === 200 || status === 201) {
      toast.success(`Template ${isEdit ? "Updated" : "Created"} Successfully!`);
      handleClose();
      refetch();
    } else {
      toast.error(message || "Something went wrong!");
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isEdit ? "Edit Template" : "Create Template"}</h2>
          <button onClick={handleClose}><CloseOutlined /></button>
        </div>

        <div className="space-y-4">
          <FormControl fullWidth>
            <label className="mb-1 text-sm font-semibold">Template Name</label>
            <CustomizeTextField
              placeholder="Enter Template Name"
              className="bg-gray-100 rounded-lg"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </FormControl>



          <button
            onClick={handleSubmit}
            className="w-full bg-purple-600 text-white py-2 rounded-lg font-bold hover:bg-purple-700 transition"
          >
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

const DeleteTemplateModal = ({ open, handleClose, template, refetch }) => {
  const customFetch = useAuthFetch();

  const handleDelete = async () => {
    try {
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/v1/dashboard/qnaTemplate/delete`
      );

      const body = {
        qnaTemplateId: template?.qnaTemplateId,
      };

      const { status, message } = await customFetch(url, "DELETE", body);

      if (status === 200 || status === 201) {
        toast.success("Template Deleted Successfully!");
        handleClose();
        refetch();
      } else {
        toast.error(message || "Something went wrong!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong!");
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Delete Template</h2>
          <button onClick={handleClose}><CloseOutlined /></button>
        </div>

        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete the template <strong>{template?.name}</strong>? This action cannot be undone.
          </p>

          <div className="flex gap-4">
            <button
              onClick={handleClose}
              className="flex-1 border border-gray-300 py-2 rounded-lg font-bold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="flex-1 bg-red-600 text-white py-2 rounded-lg font-bold hover:bg-red-700 transition"
            >
              Delete
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

const QuestionTemplateList = () => {
  const navigate = useNavigate();
  const customFetch = useAuthFetch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [qnaCounts, setQnaCounts] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editData, setEditData] = useState(null);

  const [debouncedSearch, searchQuery, setSearchQuery] = useDebounce("", 500);

  const fetchController = React.useRef(null);

  const fetchTemplates = async () => {
    setIsLoading(true);

    // Create unique ID for this request
    const requestId = Symbol();
    fetchController.current = requestId;

    try {
      const url = new URL(
        `${process.env.REACT_APP_API_URL}/v1/dashboard/qnaTemplate/list`
      );

      const params = {
        page: page + 1,
        perPage: rowsPerPage
      }

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      url.search = new URLSearchParams(params).toString();

      const { response, status } = await customFetch(url, "GET");

      // Check if this is still the most recent request
      if (fetchController.current === requestId) {
        if (status === 200) {
          console.log("RES", response)
          setData(response);
          // if (response?.data) {
          //   fetchCounts(response.data);
          // }
        } else {
          console.error("Error fetching templates");
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Only turn off loading if this was the last request
      if (fetchController.current === requestId) {
        setIsLoading(false);
      }
    }
  };

  const fetchCounts = async (templates) => {
    const counts = {};
    await Promise.all(templates.map(async (tmpl) => {
      try {
        const url = new URL(`${process.env.REACT_APP_API_URL}/v1/dashboard/qnaTemplate/list`);
        url.searchParams.append("templateId", tmpl.qnaTemplateId);
        url.searchParams.append("perPage", 1);
        const { response, status } = await customFetch(url, "GET");
        if (status === 200 && response?.pageInfo) {
          counts[tmpl.qnaTemplateId] = response.pageInfo.total;
        } else {
          counts[tmpl.qnaTemplateId] = 0;
        }
      } catch (e) {
        counts[tmpl.qnaTemplateId] = 0;
      }
    }));
    setQnaCounts(prev => ({ ...prev, ...counts }));
  }

  useEffect(() => {
    fetchTemplates();
  }, [page, rowsPerPage, debouncedSearch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = ["Qna Template id", "Name", "Product Code", "Actions"];

  return (
    <div className="py-1 px-4" style={{ height: `calc(100vh - 100px)` }}>
      <div className="flex flex-row mb-4 items-center justify-between">
        <p className="text-xl mb-0 font-extrabold">
          Question - Answer Templates
        </p>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Search Template..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:border-purple-500"
          />

          <button
            onClick={fetchTemplates}
            className="border focus:border-blue-600 flex items-center border-[#E0E0E0] mx-2 rounded py-1.5 px-3"
          >
            Refresh Data
          </button>
          <button
            onClick={() => {
              setEditData(null);
              setIsModalOpen(true);
            }}
            className="bg-purple-600 text-white flex items-center py-2 px-4 font-bold rounded hover:bg-purple-700"
          >
            <Add fontSize="small" className="mr-1" /> Create
          </button>
        </div>
      </div>

      {!data && !isLoading ? (
        <NoDataTable columns={columns} message="Server Error" />
      ) : data?.data?.length === 0 && !isLoading ? (
        <NoDataTable columns={columns} message="No templates found." />
      ) : (
        <CustomTable>
          <TableHeader columns={columns} />
          <MainTableBody isLoading={isLoading}>
            {isLoading && !data
              ? [1, 2, 3, 4, 5].map((i) => <LoadingTableSkeleton key={i} index={i} cols={columns.length} />)
              : data?.data?.map((row, i) => (
                <CustomTableRow
                  key={i}
                  columns={[
                    { text: row.qnaTemplateId },
                    { text: row.name || "-" },
                    { text: row.productCode || "-" },
                    {
                      actions: [
                        () => (
                          <button
                            onClick={() => navigate(`/dashboard/question-answer/${row.qnaTemplateId}?name=${encodeURIComponent(row.name)}`)}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Questions"
                          >
                            <VisibilityIcon /> View
                          </button>
                        ),
                        () => (
                          <button
                            onClick={() => {
                              setEditData(row);
                              setIsModalOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 ml-4"
                            title="Edit Template"
                          >
                            <EditOutlined /> Edit
                          </button>
                        ),
                        () => (
                          <button
                            onClick={() => {
                              setSelectedTemplate(row);
                              setDeleteModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-800 ml-4"
                            title="Delete Template"
                          >
                            <DeleteOutline /> Delete
                          </button>
                        ),
                      ],
                    },
                  ]}
                />
              ))}
          </MainTableBody>
          {data?.pageInfo && (
            <TablePagination
              className="sticky bottom-0 bg-white"
              component="div"
              count={data.pageInfo.total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CustomTable>
      )}

      <TemplateModal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        refetch={fetchTemplates}
        isEdit={!!editData}
        initialData={editData}
      />

      <DeleteTemplateModal
        open={deleteModalOpen}
        handleClose={() => {
          setDeleteModalOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        refetch={fetchTemplates}
      />
    </div>
  );
};

export default QuestionTemplateList;
